/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Divider, Grid, IconButton } from '@mui/material';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { ObjectProperty } from 'views/backoffice/CustomObjects/types';
import { CloseOutlined } from '@mui/icons-material';
import { TemporalAlert } from 'ui-component/records/utils/temporalAlert';
import { formatLabel, pinWay } from 'ui-component/RecordView/PropertiesPanel/utils';
import { PanelLoader } from 'ui-component/RecordView/components/PanelLoader';
import { RenderViewField } from 'ui-component/RecordView/PropertiesPanel/ViewPanel/RenderViewField';
import { useContext, useState } from 'react';
import { PanelFormsContext } from 'contexts/panelFormsContext';

interface ObjectPanelViewProps {
    loadingObjectProperties: boolean;
    handleClosePanel: () => void;
    objectProperties: (ObjectProperty & { value: string; objectName: string })[];
    isRecordViewer?: boolean;
    objectEditingRestricted?: boolean;
    showFieldMenu?: boolean;
    rightHandPanelWidth: number;
    PanelTitle?: (rightHandPanelWidth: number) => React.ReactNode;
}

const ObjectPanelView = ({
    loadingObjectProperties,
    handleClosePanel,
    objectProperties,
    isRecordViewer = false,
    objectEditingRestricted = false,
    showFieldMenu = true,
    rightHandPanelWidth,
    PanelTitle
}: ObjectPanelViewProps) => {
    const theme = useTheme();
    const defaultValues: { [key: string]: string } = {};
    objectProperties.forEach((item) => {
        defaultValues[item.name] = item.value;
    });
    const { setCustomObjectFieldsOrder: onReorderFields, enableDragAndDrop, pinnedFields } = useContext(PanelFormsContext);

    const [showTemporalAlert, setShowTemporalAlert] = useState(true);

    const handleDoubleClick = () => {
        if (objectEditingRestricted) {
            setShowTemporalAlert(true);
        }
    };

    const pinnedUpFieldEntries = pinnedFields
        .filter((fieldName) => fieldName.includes(pinWay.up))
        .map((fieldName) =>
            objectProperties.find((item) => {
                const propertyFieldName = `${item.objectName};;${item.name}`;
                return propertyFieldName === fieldName.replace(pinWay.up, '');
            })
        )
        .filter(Boolean) as (ObjectProperty & { value: string; objectName: string })[];

    const unpinnedFieldEntries = objectProperties.filter((item) => {
        const fieldId = `${item.objectName};;${item.name}`;
        return !pinnedFields.some(
            (pinnedField) =>
                pinnedField.includes(fieldId) || pinnedField.includes(fieldId + pinWay.up) || pinnedField.includes(fieldId + pinWay.down)
        );
    });

    const pinnedDownFieldEntries = pinnedFields
        .filter((fieldName) => fieldName.includes(pinWay.down))
        .map((fieldName) =>
            objectProperties.find((item) => {
                const propertyFieldName = `${item.objectName};;${item.name}`;
                return propertyFieldName === fieldName.replace(pinWay.down, '');
            })
        )
        .filter(Boolean) as (ObjectProperty & { value: string; objectName: string })[];

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || !onReorderFields) return;
        if (result.destination.index === result.source.index) return;

        const [reorderedItem] = unpinnedFieldEntries.splice(result.source.index, 1);
        unpinnedFieldEntries.splice(result.destination.index, 0, reorderedItem);

        onReorderFields(unpinnedFieldEntries.map((item) => `${item.objectName}:::${item.name}`));
    };

    if (!enableDragAndDrop) {
        return (
            <Box height="100%" width="100%" display="flex" onDoubleClick={handleDoubleClick}>
                <Box width="100%" height="100%">
                    {PanelTitle ? PanelTitle(rightHandPanelWidth) : null}
                    <Box display="flex" flexDirection="row" width="100%" height="100%">
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                overflowY: 'auto'
                            }}
                        >
                            {loadingObjectProperties ? (
                                <PanelLoader />
                            ) : (
                                <Grid
                                    container
                                    data-testid="object-details-list-view"
                                    sx={{
                                        height: '100%',
                                        flexDirection: 'column',
                                        position: 'relative'
                                    }}
                                >
                                    {pinnedUpFieldEntries.length > 0 && (
                                        <Grid item sx={{ width: '100%', maxWidth: '100%' }}>
                                            {pinnedUpFieldEntries.map((item) => (
                                                <RenderViewField
                                                    key={item.id}
                                                    field={{
                                                        ...item,
                                                        label: item.name,
                                                        dataType: item.dataType as any,
                                                        customLabel: formatLabel(item.name)
                                                    }}
                                                    handleOpenViewObjectPanel={() => {}}
                                                    showFieldMenu={showFieldMenu}
                                                    isDraggable={false}
                                                    rightHandPanelWidth={rightHandPanelWidth}
                                                    objectName={(item as any).objectName}
                                                />
                                            ))}
                                            <Divider sx={{ width: '100%', my: '0.5rem' }} />
                                        </Grid>
                                    )}
                                    <Grid
                                        item
                                        sx={{
                                            width: '100%',
                                            pl: '0.8745rem',
                                            pr: '0.5rem',
                                            flex: 1,
                                            overflowY: 'auto'
                                        }}
                                    >
                                        {unpinnedFieldEntries.map((item) => (
                                            <RenderViewField
                                                key={item.id}
                                                field={{
                                                    ...item,
                                                    label: item.name,
                                                    dataType: item.dataType as any,
                                                    customLabel: formatLabel(item.name)
                                                }}
                                                handleOpenViewObjectPanel={() => {}}
                                                showFieldMenu={showFieldMenu}
                                                isDraggable={false}
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                objectName={(item as any).objectName}
                                            />
                                        ))}
                                    </Grid>
                                    {pinnedDownFieldEntries.length > 0 && (
                                        <Grid item sx={{ width: '100%', maxWidth: '100%' }}>
                                            {pinnedDownFieldEntries.map((item) => (
                                                <RenderViewField
                                                    key={item.id}
                                                    field={{
                                                        ...item,
                                                        label: item.name,
                                                        dataType: item.dataType as any,
                                                        customLabel: formatLabel(item.name)
                                                    }}
                                                    handleOpenViewObjectPanel={() => {}}
                                                    showFieldMenu={showFieldMenu}
                                                    isDraggable={false}
                                                    rightHandPanelWidth={rightHandPanelWidth}
                                                    objectName={(item as any).objectName}
                                                />
                                            ))}
                                            <Divider sx={{ width: '100%', my: '0.5rem' }} />
                                        </Grid>
                                    )}
                                </Grid>
                            )}

                            {objectEditingRestricted && showTemporalAlert && (
                                <Box
                                    sx={{
                                        marginTop: 'auto',
                                        marginBottom: !isRecordViewer ? '3.5rem' : undefined,
                                        padding: '0.25rem',
                                        position: isRecordViewer ? 'absolute' : undefined,
                                        bottom: isRecordViewer ? '0.5rem' : undefined,
                                        width: !isRecordViewer ? '100%' : `calc(${rightHandPanelWidth}px - 7.5rem)`
                                    }}
                                >
                                    <TemporalAlert
                                        message="These fields are read-only. Please contact your administrator to request access."
                                        title="Editing Restricted"
                                        onClose={() => setShowTemporalAlert(false)}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
                {!isRecordViewer && (
                    <Box
                        sx={{
                            width: '3.25rem',
                            backgroundColor: '#F5F6F7',
                            height: '100%',
                            display: 'flex',
                            py: '0.75rem',
                            px: '0.3rem',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                        }}
                    >
                        <IconButton sx={{ backgroundColor: theme.palette.background.paper }} onClick={handleClosePanel}>
                            <CloseOutlined />
                        </IconButton>
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="edit-object-panel-fields">
                {(provided) => (
                    <Box height="100%" width="100%" display="flex" data-testid="object-panel-view">
                        <Box width="100%" height="100%">
                            {PanelTitle ? PanelTitle(rightHandPanelWidth || 0) : null}

                            <Grid
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                sx={{
                                    height: '100%',
                                    flexDirection: 'column',
                                    position: 'relative'
                                }}
                            >
                                {pinnedUpFieldEntries.length > 0 && (
                                    <Grid
                                        data-testid="object-panel-view-pinned-up-grid"
                                        item
                                        sx={{
                                            pl: '0.8745rem',
                                            pr: '0.5rem',
                                            width: '100%',
                                            maxWidth: '100%',
                                            position: 'sticky',
                                            top: 0,
                                            backgroundColor: 'background.paper',
                                            zIndex: 1
                                        }}
                                    >
                                        {pinnedUpFieldEntries.map((item) => (
                                            <RenderViewField
                                                key={item.id}
                                                field={{
                                                    ...item,
                                                    label: item.name,
                                                    dataType: item.dataType as any,
                                                    customLabel: formatLabel(item.name)
                                                }}
                                                handleOpenViewObjectPanel={() => {}}
                                                showFieldMenu={showFieldMenu}
                                                isDraggable={false}
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                objectName={(item as any).objectName}
                                            />
                                        ))}
                                        <Divider sx={{ width: '100%', my: '0.5rem' }} />
                                    </Grid>
                                )}
                                <Grid
                                    data-testid="object-panel-view-unpinned-grid"
                                    item
                                    sx={{
                                        width: '100%',
                                        pl: '0.8745rem',
                                        pr: '0.5rem',
                                        flex: 1,
                                        overflowY: 'auto'
                                    }}
                                >
                                    {unpinnedFieldEntries.map((item) => (
                                        <RenderViewField
                                            key={item.id}
                                            field={{
                                                ...item,
                                                label: item.name,
                                                dataType: item.dataType as any,
                                                customLabel: formatLabel(item.name)
                                            }}
                                            handleOpenViewObjectPanel={() => {}}
                                            showFieldMenu={showFieldMenu}
                                            isDraggable={false}
                                            rightHandPanelWidth={rightHandPanelWidth}
                                            objectName={(item as any).objectName}
                                        />
                                    ))}
                                </Grid>
                                {pinnedDownFieldEntries.length > 0 && (
                                    <Grid
                                        item
                                        sx={{
                                            pl: '0.8745rem',
                                            pr: '0.5rem',
                                            width: '100%',
                                            maxWidth: '100%',
                                            position: 'sticky',
                                            bottom: 0,
                                            backgroundColor: 'background.paper',
                                            zIndex: 1
                                        }}
                                    >
                                        <Divider sx={{ width: '100%', my: '0.5rem' }} />
                                        {pinnedDownFieldEntries.map((item) => (
                                            <RenderViewField
                                                key={item.id}
                                                field={{
                                                    ...item,
                                                    label: item.name,
                                                    dataType: item.dataType as any,
                                                    customLabel: formatLabel(item.name)
                                                }}
                                                handleOpenViewObjectPanel={() => {}}
                                                showFieldMenu={showFieldMenu}
                                                isDraggable={false}
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                objectName={(item as any).objectName}
                                            />
                                        ))}
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Box>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default ObjectPanelView;
